/**
 * How complete the page is: a ring with the API's score (0–100) and the
 * checklist built from `completeness.missing`, each item a link to the card
 * that fixes it. The score is the server's; the portal never computes it.
 */
import type { HostedPageCompletenessKey } from '../api/types';
import { useSession } from '../auth/session';
import { Link } from '../router';
import { useQuery } from '../ui/hooks';
import { COMPLETENESS_LABELS, COMPLETENESS_TARGET } from './model';

export function clampScore(score: number | null | undefined): number {
  const n = Number(score);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 0;
}

export function CompletenessRing({ score, size = 72 }: { score: number; size?: number }) {
  const s = clampScore(score);
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${s}% complete`} style={{ flex: 'none' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={s >= 100 ? 'var(--ok)' : 'var(--accent)'}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${(c * s) / 100} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={size / 4.2} fontWeight={700} fill="var(--ink)" aria-hidden="true">
        {s}%
      </text>
    </svg>
  );
}

export function CompletenessChecklist({ missing }: { missing: HostedPageCompletenessKey[] }) {
  const known = missing.filter((k) => k in COMPLETENESS_LABELS);
  if (!known.length) {
    return (
      <p className="small" style={{ margin: 0 }}>
        Everything visitors look for is on your page.
      </p>
    );
  }
  return (
    <ul className="stack-sm" aria-label="To finish your page" style={{ margin: 0, paddingLeft: 18 }}>
      {known.map((k) => (
        <li key={k}>
          <a
            href={`#${COMPLETENESS_TARGET[k]}`}
            onClick={(e) => {
              const el = document.getElementById(COMPLETENESS_TARGET[k]);
              if (!el) return;
              e.preventDefault();
              el.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
              el.focus({ preventScroll: true });
            }}
          >
            {COMPLETENESS_LABELS[k]}
          </a>
        </li>
      ))}
    </ul>
  );
}

/** "{score}% complete · Complete your page →" for the page cards and onboarding. */
export function CompleteYourPageLink({ slug, score }: { slug: string; score?: number | null }) {
  return (
    <p className="small" style={{ margin: 0 }}>
      {typeof score === 'number' ? <span>{clampScore(score)}% complete · </span> : null}
      <Link to={`/pages/${encodeURIComponent(slug)}`}>
        Complete your page<span aria-hidden="true"> →</span>
      </Link>
    </p>
  );
}

/**
 * The page cards' line: the score from the profile GET (read for an enabled
 * page only — a switched-off page shows the link alone), then the link. A
 * failed or unavailable read (an older API) drops the score, not the link.
 */
export function PageCompletenessLink({ slug, enabled }: { slug: string; enabled: boolean }) {
  const { api } = useSession();
  const profile = useQuery(() => api.getHostedPageProfile(slug), ['hosted-page-score', slug], { enabled });
  const score = enabled && profile.data && !profile.error ? profile.data.completeness?.score : null;
  return <CompleteYourPageLink slug={slug} score={score} />;
}
